import React from 'react';
import { authorColorVar } from './RedlineView';

/**
 * The change log: what was added or removed, and by whom.
 *
 * The compact alternative to RedlineView, which renders the whole document with
 * the edits marked inside it. Each entry here is one contiguous edit by one
 * person, straight from `changed_runs` on the redline response.
 *
 * Where each belongs:
 *  - The edit pages default to the inline RedlineView and offer this behind the
 *    Redline | List toggle. Showing an author WHERE in the policy something
 *    moved is the point of tracked changes; the list is for a long policy with
 *    scattered edits. (GitHub #37 made this the default instead, but the bug it
 *    actually reported was the preview endpoint diffing against the EMPTY
 *    STRING -- which made every token an insertion. That is fixed in
 *    routers/documents.py; the baseline is now the current cycle's submission,
 *    else the draft's first saved version.)
 *  - One expanded Version History row uses this and NOT RedlineView, and there
 *    #37's reasoning holds verbatim: one save on a long policy is a couple of
 *    edits, and re-rendering the whole policy per row is what that issue was
 *    about.
 *
 * Renders `run.text` as a TEXT NODE -- no marked, no DOMPurify, no
 * dangerouslySetInnerHTML. The `<ins>`/`<del>` markup surface stays confined
 * to RedlineView.js and its wider allowlist; nothing here can render markup
 * even if the server were somehow made to send some.
 */
function ChangeLogList({ runs, emptyText = 'No changes recorded yet.' }) {
  if (!runs || runs.length === 0) {
    return <div className="changelog-empty">{emptyText}</div>;
  }

  return (
    <ul className="changelog">
      {runs.map((run, index) => {
        const colorVar = authorColorVar(run.author_key);
        const added = run.status === 'inserted';
        return (
          // No stable id: these are derived spans, recomputed on every keystroke,
          // and the list is fully replaced each time rather than reordered.
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} className={`changelog-entry changelog-entry--${added ? 'added' : 'removed'}`}>
            <span className="changelog-sign" aria-hidden="true">{added ? '+' : '−'}</span>
            <div className="changelog-body">
              <span className="changelog-text">
                <span className="sr-only">{added ? 'Added: ' : 'Removed: '}</span>
                {run.text}
              </span>
              <span className="changelog-meta">
                {run.author_name && (
                  <span
                    className="changelog-author-dot"
                    style={colorVar ? { background: `var(${colorVar})` } : undefined}
                    aria-hidden="true"
                  />
                )}
                {run.author_name || 'Unknown author'}
                {run.at && ` · ${new Date(run.at).toLocaleString()}`}
                {run.truncated && ` · ${run.token_count} tokens`}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default ChangeLogList;
